import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI, attendanceAPI, probationAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import { DatePicker } from '../../components/ui/date-picker';
import {
  Users,
  Clock,
  FileText,
  IndianRupee,
  Home,
  CheckCircle,
  XCircle,
  Eye,
  CalendarOff,
  Activity,
  ChevronRight,
  MapPin,
  ShieldAlert,
  ScrollText,
  Pencil,
  UserCheck,
  AlertTriangle,
  Trash2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  present: 'bg-green-100 text-green-700',
  absent: 'bg-red-100 text-red-700',
  half_day_morning: 'bg-yellow-100 text-yellow-700',
  half_day_afternoon: 'bg-yellow-100 text-yellow-700',
  on_leave: 'bg-blue-100 text-blue-700',
  not_marked: 'bg-slate-100 text-slate-500',
};

const STATUS_LABELS = {
  present: 'Present',
  absent: 'Absent',
  half_day_morning: 'Half Day (AM)',
  half_day_afternoon: 'Half Day (PM)',
  on_leave: 'On Leave',
  not_marked: 'Not Marked',
};

const getPhotoUrl = (p) => (!p ? null : p.startsWith('http') ? p : `${BACKEND_URL}${p}`);
const getInitials = (n) => n?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'U';

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Attendance override state
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideEmployee, setOverrideEmployee] = useState(null);
  const [overrideData, setOverrideData] = useState({ status: '', admin_note: '' });
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [probationAlerts, setProbationAlerts] = useState([]);
  const [convertingId, setConvertingId] = useState(null);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [clearConfirm, setClearConfirm] = useState('');
  const [clearCategories, setClearCategories] = useState({
    attendance: false,
    leave_applications: false,
    leave_balances: false,
    expenses: false,
    pending_actions: false,
    evaluations: false,
    notifications: false,
    policies: false,
    activity_log: false,
    holidays: false,
    push_subscriptions: false,
    office_expenses: false,
    comp_off_accruals: false,
    balance_adjustments: false,
  });
  const [clearDateFrom, setClearDateFrom] = useState('');
  const [clearDateTo, setClearDateTo] = useState('');
  const [deleteEmployees, setDeleteEmployees] = useState(false);

  const fetchData = () => {
    dashboardAPI.getAdmin()
      .then(res => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
    probationAPI.getUpcoming()
      .then(res => setProbationAlerts(res.data.employees?.filter(e => e.days_remaining <= 30) || []))
      .catch(() => {});
  };

  const handleConvertPermanent = async (empId, empName) => {
    if (!window.confirm(`Convert ${empName} from probation to permanent?`)) return;
    setConvertingId(empId);
    try {
      await probationAPI.convertToPermanent(empId);
      setProbationAlerts(prev => prev.filter(e => e.employee_id !== empId));
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to convert');
    } finally {
      setConvertingId(null);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleClearAllData = async () => {
    if (clearConfirm !== 'DELETE') return;
    const selectedCats = Object.entries(clearCategories).filter(([, v]) => v).map(([k]) => k);
    if (selectedCats.length === 0 && !deleteEmployees) return;
    setClearing(true);
    try {
      await dashboardAPI.clearData({
        categories: selectedCats,
        date_from: clearDateFrom || undefined,
        date_to: clearDateTo || undefined,
        delete_employees: deleteEmployees,
      });
      setClearOpen(false);
      setClearConfirm('');
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to clear data');
    } finally {
      setClearing(false);
    }
  };

  const toggleAllCategories = (val) => {
    setClearCategories(prev => Object.fromEntries(Object.keys(prev).map(k => [k, val])));
  };

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const openOverride = (emp) => {
    setOverrideEmployee(emp);
    setOverrideData({ status: emp.status === 'not_marked' ? 'present' : emp.status, admin_note: '' });
    setOverrideOpen(true);
  };

  const handleOverrideSubmit = async () => {
    if (!overrideEmployee || !overrideData.status) return;
    setOverrideSaving(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      await attendanceAPI.adminUpdate({
        employee_id: overrideEmployee.employee_id,
        date: today,
        status: overrideData.status,
        admin_note: overrideData.admin_note || null,
      });
      setOverrideOpen(false);
      fetchData(); // Refresh dashboard
    } catch (e) {
      console.error('Failed to update attendance', e);
      alert(e.response?.data?.detail || 'Failed to update attendance');
    } finally {
      setOverrideSaving(false);
    }
  };

  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const formatDate = (d) => {
    const days = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const day = d.getDate();
    const suffix = day % 10 === 1 && day !== 11 ? 'st' : day % 10 === 2 && day !== 12 ? 'nd' : day % 10 === 3 && day !== 13 ? 'rd' : 'th';
    return `${days[d.getDay()]} ${day}${suffix} ${months[d.getMonth()]} ${d.getFullYear()}`;
  };
  const formatTime = (d) => {
    let h = d.getHours(), m = d.getMinutes();
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${h}:${m.toString().padStart(2,'0')} ${ampm}`;
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1D35A0]"></div>
      </div>
    );
  }

  const { stats, attendance_table, pending_leaves, pending_claims, on_leave_today, activities } = data;

  return (
    <div className="space-y-5" data-testid="admin-dashboard">
      {/* Date & Time */}
      <div className="flex items-center justify-between" data-testid="dashboard-datetime">
        <div>
          <p className="text-lg font-semibold text-[#1D35A0]" data-testid="current-date">{formatDate(now)}</p>
          <p className="text-2xl font-bold text-[#1D35A0]" data-testid="current-time">{formatTime(now)}</p>
        </div>
        {user?.email === 'vijay@eventresource.in' && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setClearOpen(true); setClearConfirm(''); }}
            className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
            data-testid="clear-all-data-btn"
          >
            <Trash2 className="w-4 h-4 mr-1.5" />
            Clear Data
          </Button>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3" data-testid="stat-present">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-[#1D35A0]">{stats.present_today}</p>
              <p className="text-[10px] text-slate-500">Present ({stats.present_percent}%)</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3" data-testid="stat-leave">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <CalendarOff className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-[#1D35A0]">{stats.on_leave_today}</p>
              <p className="text-[10px] text-slate-500">On Leave</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3" data-testid="stat-pending">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
              <Clock className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-[#1D35A0]">{stats.pending_approvals}</p>
              <p className="text-[10px] text-slate-500">Pending Approvals</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3" data-testid="stat-team">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
              <Users className="w-4 h-4 text-purple-600" />
            </div>
            <div>
              <p className="text-xl font-bold text-[#1D35A0]">{stats.total_active}</p>
              <p className="text-[10px] text-slate-500">Total Team</p>
            </div>
          </div>
        </div>
      </div>

      {/* Probation Alerts Widget */}
      {probationAlerts.length > 0 && (
        <div className="bg-white rounded-lg border border-amber-200 shadow-sm overflow-hidden" data-testid="probation-alerts-widget">
          <div className="px-5 py-3 border-b border-amber-100 bg-amber-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <h3 className="text-sm font-semibold text-amber-800">Probation Alerts ({probationAlerts.length})</h3>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/employees')} className="text-amber-700 hover:text-amber-900 h-7 text-xs" data-testid="view-all-probation-btn">
              View Employees <ChevronRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
          <div className="divide-y divide-amber-50">
            {probationAlerts.map((emp) => (
              <div key={emp.employee_id} className="px-5 py-2.5 flex items-center justify-between" data-testid={`probation-alert-${emp.employee_id}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${emp.status === 'overdue' ? 'bg-red-500' : emp.status === 'urgent' ? 'bg-amber-500' : 'bg-blue-500'}`} />
                  <div>
                    <p className="text-sm font-medium text-slate-800">{emp.employee_name}</p>
                    <p className="text-xs text-slate-400">{emp.designation} &middot; Ends {emp.probation_end_date}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                    emp.status === 'overdue' ? 'bg-red-100 text-red-700' : emp.status === 'urgent' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                  }`}>
                    {emp.days_remaining < 0 ? `${Math.abs(emp.days_remaining)}d overdue` : `${emp.days_remaining}d left`}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs text-green-700 border-green-200 hover:bg-green-50"
                    disabled={convertingId === emp.employee_id}
                    onClick={() => handleConvertPermanent(emp.employee_id, emp.employee_name)}
                    data-testid={`convert-btn-${emp.employee_id}`}
                  >
                    {convertingId === emp.employee_id ? '...' : 'Make Permanent'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          onClick={() => navigate('/admin/policies')}
          className="text-sm"
          data-testid="manage-policies-btn"
        >
          <ScrollText className="w-4 h-4 mr-2" />
          Manage Policies ({stats.policy_count || 0})
        </Button>
      </div>

      {/* Today's Attendance Table */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid="attendance-table">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-[#1D35A0]">Today's Attendance</h3>
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/attendance')} data-testid="view-all-attendance">
            View All <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-2 font-medium text-slate-600 text-xs">Employee</th>
                <th className="text-center px-3 py-2 font-medium text-slate-600 text-xs">Status</th>
                <th className="text-center px-3 py-2 font-medium text-slate-600 text-xs hidden sm:table-cell">Check In</th>
                <th className="text-center px-3 py-2 font-medium text-slate-600 text-xs hidden sm:table-cell">Check Out</th>
                <th className="text-center px-3 py-2 font-medium text-slate-600 text-xs hidden lg:table-cell">Location</th>
                <th className="text-center px-3 py-2 font-medium text-slate-600 text-xs">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {attendance_table?.map((emp) => (
                <tr key={emp.employee_id} className="hover:bg-slate-50" data-testid={`attendance-row-${emp.employee_id}`}>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2.5">
                      <Avatar className="h-7 w-7">
                        <AvatarImage src={getPhotoUrl(emp.profile_photo)} />
                        <AvatarFallback className="text-[10px] bg-slate-100">{getInitials(emp.employee_name)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-slate-900 text-xs">{emp.employee_name}</p>
                        <p className="text-[10px] text-slate-400">{emp.designation}</p>
                      </div>
                    </div>
                  </td>
                  <td className="text-center px-3 py-2">
                    <div className="flex flex-col items-center gap-0.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLORS[emp.status] || STATUS_COLORS.not_marked}`}>
                        {STATUS_LABELS[emp.status] || emp.status}
                      </span>
                      {emp.is_wfh && <span className="inline-flex items-center gap-1 text-xs text-indigo-600"><Home className="w-3 h-3" />WFH</span>}
                    </div>
                  </td>
                  <td className="text-center px-3 py-3 text-slate-600 hidden sm:table-cell">
                    {emp.check_in_time || '—'}
                  </td>
                  <td className="text-center px-3 py-3 text-slate-600 hidden sm:table-cell">
                    {emp.check_out_time || '—'}
                  </td>
                  <td className="text-center px-3 py-3 hidden lg:table-cell">
                    {emp.is_outside_geofence === true && (
                      <span className="inline-flex items-center gap-1 text-xs text-red-600">
                        <ShieldAlert className="w-3 h-3" />
                        Outside ({emp.distance_from_office}m)
                      </span>
                    )}
                    {emp.is_outside_geofence === false && (
                      <span className="inline-flex items-center gap-1 text-xs text-green-600">
                        <MapPin className="w-3 h-3" />
                        In Office
                      </span>
                    )}
                    {emp.check_in_address && !emp.is_outside_geofence && emp.is_outside_geofence !== false && (
                      <span className="text-xs text-slate-400 truncate max-w-[120px] inline-block" title={emp.check_in_address}>
                        {emp.check_in_address}
                      </span>
                    )}
                  </td>
                  <td className="text-center px-3 py-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-slate-500 hover:text-blue-600"
                      onClick={() => openOverride(emp)}
                      data-testid={`override-btn-${emp.employee_id}`}
                    >
                      <Pencil className="w-3.5 h-3.5 mr-1" />
                      <span className="text-xs">Change</span>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bottom Grid: Pending + Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Pending Actions */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200">
            <h3 className="text-base font-semibold text-[#1D35A0]">Pending Actions</h3>
          </div>
          <div className="divide-y divide-slate-100">
            {pending_leaves?.map((l) => (
              <div key={l.id} className="px-5 py-3 flex items-center justify-between" data-testid={`pending-leave-${l.id}`}>
                <div>
                  <p className="text-sm font-medium text-slate-800">{l.employee_name}</p>
                  <p className="text-xs text-slate-400">{l.leave_type_name} · {l.start_date} to {l.end_date}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => navigate('/admin/leave')} data-testid={`review-leave-${l.id}`}>
                  <Eye className="w-3.5 h-3.5 mr-1" /> Review
                </Button>
              </div>
            ))}
            {pending_claims?.map((c) => (
              <div key={c.id} className="px-5 py-3 flex items-center justify-between" data-testid={`pending-claim-${c.id}`}>
                <div>
                  <p className="text-sm font-medium text-slate-800">{c.employee_name}</p>
                  <p className="text-xs text-slate-400">Rs. {c.amount_claimed?.toLocaleString()} · {c.category}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => navigate('/admin/expenses')} data-testid={`review-claim-${c.id}`}>
                  <IndianRupee className="w-3.5 h-3.5 mr-1" /> Review
                </Button>
              </div>
            ))}
            {(!pending_leaves?.length && !pending_claims?.length) && (
              <div className="px-5 py-8 text-center text-sm text-slate-400">No pending actions</div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200">
            <h3 className="text-base font-semibold text-[#1D35A0]">Recent Activity</h3>
          </div>
          <div className="divide-y divide-slate-100">
            {activities?.map((a) => (
              <div key={a.id} className="px-5 py-3" data-testid={`activity-${a.id}`}>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-slate-700"><strong>{a.actor_name}</strong> {a.detail || a.action}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {new Date(a.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              </div>
            ))}
            {!activities?.length && (
              <div className="px-5 py-8 text-center text-sm text-slate-400">No recent activity</div>
            )}
          </div>
        </div>
      </div>

      {/* On Leave Today */}
      {on_leave_today?.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200">
            <h3 className="text-base font-semibold text-[#1D35A0]">On Leave Today</h3>
          </div>
          <div className="divide-y divide-slate-100">
            {on_leave_today.map((l) => (
              <div key={l.id} className="px-5 py-3 flex items-center justify-between">
                <p className="text-sm font-medium text-slate-800">{l.employee_name}</p>
                <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">{l.leave_type_name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Attendance Override Dialog */}
      <Dialog open={overrideOpen} onOpenChange={setOverrideOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change Attendance</DialogTitle>
            <DialogDescription>
              Update today's attendance for <strong>{overrideEmployee?.employee_name}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Current Status</Label>
              <p className="text-sm">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[overrideEmployee?.status] || STATUS_COLORS.not_marked}`}>
                  {STATUS_LABELS[overrideEmployee?.status] || 'Not Marked'}
                </span>
              </p>
            </div>
            <div className="space-y-2">
              <Label>New Status *</Label>
              <Select value={overrideData.status} onValueChange={(v) => setOverrideData(p => ({ ...p, status: v }))}>
                <SelectTrigger data-testid="override-status-select">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="present">Present</SelectItem>
                  <SelectItem value="absent">Absent</SelectItem>
                  <SelectItem value="half_day_morning">Half Day (Morning)</SelectItem>
                  <SelectItem value="half_day_afternoon">Half Day (Afternoon)</SelectItem>
                  <SelectItem value="on_leave">On Leave</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reason / Note *</Label>
              <Textarea
                value={overrideData.admin_note}
                onChange={(e) => setOverrideData(p => ({ ...p, admin_note: e.target.value }))}
                placeholder="e.g. Sick leave, Personal emergency, Half day approved..."
                rows={3}
                data-testid="override-reason-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOverrideOpen(false)}>Cancel</Button>
              <Button
                onClick={handleOverrideSubmit}
                className="bg-[#1D35A0] hover:bg-[#152A80] text-white"
                disabled={overrideSaving || !overrideData.status || !overrideData.admin_note.trim()}
                data-testid="override-submit-btn"
              >
                {overrideSaving ? 'Saving...' : 'Update Attendance'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Clear Data Dialog */}
      <Dialog open={clearOpen} onOpenChange={setClearOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Clear Data
            </DialogTitle>
            <DialogDescription>
              Select what to delete. Your account (Vijay Sabhlok) is always protected.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {/* Date Range */}
            <div className="bg-slate-50 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-slate-500 uppercase">Date Range (optional — leave blank to delete all)</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">From</Label>
                  <DatePicker value={clearDateFrom} onChange={setClearDateFrom} placeholder="From date" data-testid="clear-date-from" />
                </div>
                <div>
                  <Label className="text-xs">To</Label>
                  <DatePicker value={clearDateTo} onChange={setClearDateTo} placeholder="To date" data-testid="clear-date-to" />
                </div>
              </div>
            </div>

            {/* Category Checkboxes */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-slate-500 uppercase">Data Categories</p>
                <div className="flex gap-2">
                  <button type="button" onClick={() => toggleAllCategories(true)} className="text-xs text-blue-600 hover:underline">Select All</button>
                  <button type="button" onClick={() => toggleAllCategories(false)} className="text-xs text-slate-400 hover:underline">Clear All</button>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-1.5">
                {[
                  { key: 'attendance', label: 'Attendance Records' },
                  { key: 'leave_applications', label: 'Leave Applications' },
                  { key: 'leave_balances', label: 'Leave Balances' },
                  { key: 'expenses', label: 'Expense / Reimbursement Claims' },
                  { key: 'pending_actions', label: 'Pending Actions (Pending Leaves + Expenses)' },
                  { key: 'evaluations', label: 'Employee Evaluations' },
                  { key: 'notifications', label: 'Notifications' },
                  { key: 'policies', label: 'Policies & Notices' },
                  { key: 'activity_log', label: 'Activity Log / Recent Activity' },
                  { key: 'holidays', label: 'Holidays' },
                  { key: 'office_expenses', label: 'Office Expenses' },
                  { key: 'comp_off_accruals', label: 'Comp-off Accruals' },
                  { key: 'balance_adjustments', label: 'Leave Balance Adjustments' },
                  { key: 'push_subscriptions', label: 'Push Notification Subscriptions' },
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-slate-50 cursor-pointer" data-testid={`clear-cat-${key}`}>
                    <input
                      type="checkbox"
                      checked={clearCategories[key]}
                      onChange={(e) => setClearCategories(prev => ({ ...prev, [key]: e.target.checked }))}
                      className="rounded border-slate-300"
                    />
                    <span className="text-sm text-slate-700">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Delete Employees */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="clear-delete-employees">
                <input
                  type="checkbox"
                  checked={deleteEmployees}
                  onChange={(e) => setDeleteEmployees(e.target.checked)}
                  className="rounded border-red-300"
                />
                <span className="text-sm font-medium text-red-700">Delete all employees (except Vijay Sabhlok)</span>
              </label>
            </div>

            {/* Confirm */}
            <div className="space-y-2">
              <Label>Type <strong>DELETE</strong> to confirm</Label>
              <input
                type="text"
                value={clearConfirm}
                onChange={(e) => setClearConfirm(e.target.value)}
                placeholder="DELETE"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                data-testid="clear-confirm-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setClearOpen(false)}>Cancel</Button>
              <Button
                onClick={handleClearAllData}
                disabled={clearing || clearConfirm !== 'DELETE'}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="clear-confirm-btn"
              >
                {clearing ? 'Clearing...' : 'Delete Selected Data'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
